import * as React from 'react'
import { Route, Switch } from 'react-router'
import { MsalContext } from '@azure/msal-react'
import Layout from './components/Layout'
import Home from './components/Home'
import { ModemsPage } from './components/Modems'
import UserDetailsPage from './components/UserDetailsPage'
import Organizations from './components/Organizations'
import OrganizationDetails from './components/OrganizationDetails'
import OrganizationForm from './components/OrganizationForm';
import Sites from './components/Sites'
import { SubgroupsPage } from './components/Subgroups';
import SubgroupDetails from './components/SubgroupDetails';
import SubgroupForm from './components/SubgroupForm';
import ModemDetail from './components/ModemDetail'
import SiteDetail from './components/SiteDetail'
import { SiteDetailsForm } from './components/SiteForm'
import Transport from './components/TransportApp'
import * as TransportStore from './store/TransportApp'
import * as UsersStore from './store/Users'
import { NotFoundPage, UserNotFoundPage } from './components/NotFound'
import './custom.css'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from './store'
import Users from './components/Users';
import AddEmailToOrg from './components/AddEmailToOrg';
import AddModemToSubgroup from './components/AddModemToSubgroup';
import AddMemberForm from './components/AddMemberForm';
import CenteredProgress from './common/CenteredLoader'
import AddMemberToSubgroupForm from './components/AddMemberToSubgroupForm';
import { UsersClient, UserWithOrganizationResponseDTO } from './generated/ApiClient'
import { useMsal } from '@azure/msal-react'

// see library for more options https://www.npmjs.com/package/use-react-router-breadcrumbs#order-matters
// also note this is evaluated top-to-bottom when matching, so variables in routes need to come after constants
export const routeConfig = [
    {
        exact: true,
        path: '/',
        component: Home
    },
    {
        path: '/transporter',
        component: Transport
    },
    {
        path: '/users/:userId',
        component: UserDetailsPage
    },
    {
        path: '/users',
        component: Users
    },
    {
        path: '/modems/:modemId',
        component: ModemDetail
    },
    {
        path: '/modems',
        component: ModemsPage
    },
    {
        exact: true,
        path: '/organizations/create',
        component: OrganizationForm
    },
    {
        path: '/organizations/:organizationId/emails/create',
        component: AddEmailToOrg
    },
    {
        path: '/organizations/:organizationId/subgroups/create',
        component: SubgroupForm
    },
    {
        path: '/organizations/:organizationId/subgroups/:subgroupId/addModem',
        component: AddModemToSubgroup
    },
    {
        path: '/organizations/:organizationId/subgroups/:subgroupId/addMember',
        component: AddMemberToSubgroupForm
    },
    {
        path: '/organizations/:organizationId/subgroups/:subgroupId',
        component: SubgroupDetails
    },
    {
        path: '/organizations/:organizationId/subgroups',
        component: SubgroupsPage
    },
    {
        path: '/organizations/:organizationId/addMember',
        component: AddMemberForm
    },
    {
        path: '/organizations/:organizationId/addSite',
        component: SiteDetailsForm
    },
    {
        path: '/organizations/:organizationId/sites/:siteId',
        component: SiteDetail
    },
    {
        path: '/organizations/:organizationId',
        component: OrganizationDetails
    },
    {
        path: '/organizations',
        component: Organizations
    },
    {
        exact: true,
        path: '/sites/create',
        component: SiteDetailsForm
    },
    {
        path: '/sites/:siteId',
        component: SiteDetail
    },
    {
        path: '/sites',
        component: Sites
    },
    {
        component: NotFoundPage
    }
]

export default function App() {
    const [loading, setLoading] = React.useState<'Loading' | 'Loaded' | 'Failed'>('Loading')
    const dispatch = useDispatch()
    const { instance } = useMsal()
    const localAccountId = instance.getActiveAccount()!.localAccountId
    const user = useSelector<ApplicationState, UserWithOrganizationResponseDTO | undefined>(state => {
        return state.users?.byId[localAccountId]
    })
    // Set the context type for consuming context provided by MsalProvider component
    // https://reactjs.org/docs/context.html#classcontexttype
    //static contextType = MsalContext;

    // load current authenticated user data
    React.useEffect(() => {
        new UsersClient().persistUser().then(() =>
            dispatch(UsersStore.actionCreators.requestUser(localAccountId)))
            .catch(() => setLoading('Failed'))
    }, [])
    React.useEffect(() => {
        // start loading the transport config so that it is ambient across all routes
        dispatch(TransportStore.actionCreators.requestTransportConfig())
    }, [])

    return (
        <Layout>
            {user !== undefined ?
                <Switch>
                    {routeConfig.map(route => <Route key={route.path ?? ''} exact={route.exact} path={route.path} component={route.component} />)}
                </Switch>
                : loading === 'Failed' ?
                    <UserNotFoundPage />
                    : // Prevent application from rendering until user data is fetched
                    <CenteredProgress />}
        </Layout>
    )
}
